/**
 * Cognito authentication client
 * Uses Cognito USER_PASSWORD_AUTH flow directly without AWS SDK
 */

const COGNITO_REGION = import.meta.env.VITE_AWS_REGION || 'us-west-1'
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || ''

const COGNITO_URL = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`

export interface AuthTokens {
  accessToken: string
  idToken: string
  refreshToken: string
  expiresIn: number
}

export interface CognitoError {
  code: string
  message: string
}

interface CognitoResponse {
  AuthenticationResult?: {
    AccessToken: string
    IdToken: string
    RefreshToken: string
    ExpiresIn: number
  }
  ChallengeName?: string
  Session?: string
  ChallengeParameters?: Record<string, string>
}

async function cognitoRequest(action: string, payload: Record<string, unknown>): Promise<unknown> {
  const response = await fetch(COGNITO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  })

  const data = await response.json()

  if (!response.ok) {
    const error: CognitoError = {
      code: data.__type?.split('#')[1] || 'UnknownError',
      message: data.message || 'Authentication failed',
    }
    throw error
  }

  return data
}

export async function signIn(email: string, password: string): Promise<AuthTokens | { challengeName: string; session: string }> {
  const data = await cognitoRequest('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  }) as CognitoResponse

  // Handle NEW_PASSWORD_REQUIRED challenge (first login with temp password)
  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    return {
      challengeName: data.ChallengeName,
      session: data.Session || '',
    }
  }

  if (!data.AuthenticationResult) {
    throw { code: 'AuthError', message: 'No authentication result' }
  }

  return {
    accessToken: data.AuthenticationResult.AccessToken,
    idToken: data.AuthenticationResult.IdToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresIn: data.AuthenticationResult.ExpiresIn,
  }
}

export async function respondToNewPasswordChallenge(
  email: string,
  newPassword: string,
  session: string
): Promise<AuthTokens> {
  const data = await cognitoRequest('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
    },
  }) as CognitoResponse

  if (!data.AuthenticationResult) {
    throw { code: 'AuthError', message: 'Failed to set new password' }
  }

  return {
    accessToken: data.AuthenticationResult.AccessToken,
    idToken: data.AuthenticationResult.IdToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresIn: data.AuthenticationResult.ExpiresIn,
  }
}

export async function refreshTokens(refreshToken: string): Promise<Omit<AuthTokens, 'refreshToken'>> {
  const data = await cognitoRequest('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
    },
  }) as CognitoResponse

  if (!data.AuthenticationResult) {
    throw { code: 'AuthError', message: 'Failed to refresh tokens' }
  }

  return {
    accessToken: data.AuthenticationResult.AccessToken,
    idToken: data.AuthenticationResult.IdToken,
    expiresIn: data.AuthenticationResult.ExpiresIn,
  }
}

export async function signOut(accessToken: string): Promise<void> {
  await cognitoRequest('GlobalSignOut', {
    AccessToken: accessToken,
  })
}

// Token storage keys
const STORAGE_KEYS = {
  accessToken: 'underwriter_access_token',
  idToken: 'underwriter_id_token',
  refreshToken: 'underwriter_refresh_token',
  expiresAt: 'underwriter_expires_at',
}

// Helper to check if localStorage is properly available
function isLocalStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && typeof localStorage?.getItem === 'function'
  } catch {
    return false
  }
}

export function saveTokens(tokens: AuthTokens): void {
  if (!isLocalStorageAvailable()) return

  localStorage.setItem(STORAGE_KEYS.accessToken, tokens.accessToken)
  localStorage.setItem(STORAGE_KEYS.idToken, tokens.idToken)
  localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refreshToken)
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(Date.now() + tokens.expiresIn * 1000))
}

export function getStoredTokens(): { accessToken: string; idToken: string; refreshToken: string; expiresAt: number } | null {
  if (!isLocalStorageAvailable()) return null

  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken)
  const idToken = localStorage.getItem(STORAGE_KEYS.idToken)
  const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken)
  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt)

  if (!accessToken || !idToken || !refreshToken || !expiresAt) {
    return null
  }

  return {
    accessToken,
    idToken,
    refreshToken,
    expiresAt: parseInt(expiresAt, 10),
  }
}

export function clearStoredTokens(): void {
  if (!isLocalStorageAvailable()) return

  localStorage.removeItem(STORAGE_KEYS.accessToken)
  localStorage.removeItem(STORAGE_KEYS.idToken)
  localStorage.removeItem(STORAGE_KEYS.refreshToken)
  localStorage.removeItem(STORAGE_KEYS.expiresAt)
}

export function isTokenExpired(expiresAt: number): boolean {
  // Consider expired 5 minutes before actual expiry
  return Date.now() > expiresAt - 5 * 60 * 1000
}

// Cookie helpers for auth state
const AUTH_COOKIE_NAME = 'underwriter-auth'

export function setAuthCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${AUTH_COOKIE_NAME}=authenticated; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`
}

export function clearAuthCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${AUTH_COOKIE_NAME}=; path=/; max-age=0`
}
